import { createSelector } from "reselect"
import { get, map, chain, pick, sumBy, find, set, isEmpty } from "lodash"
import { buildContributorKey } from "helpers/contributors"
import { convertImpliedReturnsIdToUniversalId } from "helpers/securities"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

const getImpliedReturns = state => get(state, "global.data.impliedReturnsForecast.data.model.simulation.forecast.forecast", EMPTY_OBJECT)
const getModuleForecast = state => get(state, "modules.rebalance.forecast")
const getHorizon = state => get(state, "modules.rebalance.horizon")

// Contributors
const getRawForecastContributors = state => get(state, "global.data.forecastContributors.data.model.simulation.forecastContributors.contributors", EMPTY_ARRAY)
const getRawRebalancedForecastContributors = state => get(state, "modules.rebalance.rebalancedForecastContributors", EMPTY_ARRAY)

// Grouped Contributors
const getRawGroupedForecastContributors = state => get(state, "global.data.groupedForecastContributors.data.model.simulation.groupedForecastContributors.groupedContributors", EMPTY_ARRAY)
const getRawGroupedRebalancedForecastContributors = state => get(state, "modules.rebalance.rebalancedGroupedForecastContributors", EMPTY_ARRAY)

export const getForecasts = state => get(state, "global.data.forecasts.data.forecasts", EMPTY_ARRAY)

// getModuleForecast's value can come from user preferences. To make sure that the forecast is still available,
// we attempt to find it in the list of available forecasts.
export const getForecast = createSelector(
  [getModuleForecast, getHorizon, getForecasts],
  (forecast, horizon, forecasts) => ({
    forecast: find(forecasts, ({ id }) => id === get(forecast, "id")),
    horizon,
  }),
)

export const getForecastSecurities = createSelector(
  [getImpliedReturns],
  computeForecastSecurities,
)

export const getForecastContributors = createSelector(
  [getRawForecastContributors],
  computeForecastContributors,
)

export const getGroupedForecastContributors = createSelector(
  [getRawGroupedForecastContributors],
  computeForecastContributors,
)

export const getRebalancedForecastContributors = createSelector(
  [getRawRebalancedForecastContributors],
  computeForecastContributors,
)

export const getGroupedRebalancedForecastContributors = createSelector(
  [getRawGroupedRebalancedForecastContributors],
  computeForecastContributors,
)

// We can't use the forecast.total as a summary number when using impliedReturns and rebalancing
// so instead we sum the weightedExpectedReturn of all contributors and for simplicity's sake,
// we do this regardless of using impliedReturns or not
export const getForecastContributorsTotal = createSelector(
  [getRawForecastContributors],
  computeForecastContributorsTotal,
)

export const getRebalancedForecastContributorsTotal = createSelector(
  [getRawRebalancedForecastContributors],
  computeForecastContributorsTotal,
)

function computeForecastSecurities(data) {
  const { horizon } = data ?? {}
  return chain(data)
    .map((assetGroups, assetClass) => map(assetGroups, position => {
      const adjustedPosition = position
      if (assetClass === "indexes") {
        set(adjustedPosition, "id", { indexId: position.id })
      }
      return ({
        // modelProviderId is not accepted in the UniversalId that's being used for the forecastContributors query
        id: convertImpliedReturnsIdToUniversalId(adjustedPosition),
        expectedReturn: {
          return: adjustedPosition.expectedPercentReturn,
          horizon,
        },
      })
    }))
    .flatten()
    .value()
}

function computeForecastContributors(forecastContributors) {
  return chain(forecastContributors)
    .keyBy(buildContributorKey)
    .mapValues((contributor) => ({
      ...pick(contributor, ["totalExpectedReturn", "securityExpectedReturn", "weightedExpectedReturn"]),
      ...(contributor.contributors && { contributors: computeForecastContributors(contributor.contributors) }),
    }))
    .value()
}

function computeForecastContributorsTotal(forecastContributors) {
  if (isEmpty(forecastContributors)) {
    return NaN
  }
  return sumBy(forecastContributors, (contributor) => {
    if (contributor.contributors) {
      return computeForecastContributorsTotal(contributor.contributors)
    }
    return contributor.weightedExpectedReturn
  })
}
